import {
  FilterField,
  FieldType,
  FilterOperator,
  TextOperator,
  NumberOperator,
  DateOperator,
  FilterRow,
  FilterLogic,
  ActiveFilterChip,
} from "@/types/filters";

// Field definitions for Stock Takes
export const STOCK_TAKES_TEXT_FIELDS: FilterField[] = [
  { id: "id", label: "ID", type: "text" },
  { id: "reference", label: "Reference", type: "text" },
  { id: "description", label: "Description", type: "text" },
  { id: "type", label: "Type", type: "text" },
  { id: "status", label: "Status", type: "text" },
  { id: "warehouse", label: "Warehouse", type: "text" },
  { id: "warehouseCode", label: "Warehouse Code", type: "text" },
  { id: "assignedTo", label: "Assigned To", type: "text" },
  { id: "createdBy", label: "Created By", type: "text" },
  { id: "recurrence", label: "Recurrence", type: "text" },
  { id: "abcClass", label: "ABC Class", type: "text" },
  { id: "notes", label: "Notes", type: "text" },
];

export const STOCK_TAKES_NUMBER_FIELDS: FilterField[] = [
  { id: "totalItems", label: "Total Items", type: "number" },
  { id: "countedItems", label: "Counted Items", type: "number" },
  { id: "varianceCount", label: "Discrepancies", type: "number" },
];

export const STOCK_TAKES_DATE_FIELDS: FilterField[] = [
  { id: "createdAt", label: "Created Date", type: "date" },
  { id: "startedAt", label: "Started Date", type: "date" },
  { id: "completedAt", label: "Completed Date", type: "date" },
  { id: "nextScheduledDate", label: "Next Scheduled Date", type: "date" },
  { id: "scheduledDate", label: "Scheduled Date", type: "date" },
];

export const STOCK_TAKES_ALL_FIELDS: FilterField[] = [
  ...STOCK_TAKES_TEXT_FIELDS,
  ...STOCK_TAKES_NUMBER_FIELDS,
  ...STOCK_TAKES_DATE_FIELDS,
];

// Operator options
export interface OperatorOption {
  value: FilterOperator;
  label: string;
}

export const TEXT_OPERATORS: OperatorOption[] = [
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Not equals" },
  { value: "contains", label: "Contains" },
  { value: "doesNotContain", label: "Does not contain" },
  { value: "startsWith", label: "Starts with" },
  { value: "endsWith", label: "Ends with" },
  { value: "isEmpty", label: "Is empty" },
  { value: "isNotEmpty", label: "Is not empty" },
];

export const NUMBER_OPERATORS: OperatorOption[] = [
  { value: "equals", label: "Equals" },
  { value: "notEquals", label: "Not equals" },
  { value: "greaterThan", label: "Greater than" },
  { value: "greaterThanOrEqual", label: "Greater than or equal" },
  { value: "lessThan", label: "Less than" },
  { value: "lessThanOrEqual", label: "Less than or equal" },
  { value: "between", label: "Between" },
  { value: "isEmpty", label: "Is empty" },
  { value: "isNotEmpty", label: "Is not empty" },
];

export const DATE_OPERATORS: OperatorOption[] = [
  { value: "equals", label: "Equals" },
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
  { value: "between", label: "Between" },
  { value: "inLastXDays", label: "In last X days" },
  { value: "isEmpty", label: "Is empty" },
  { value: "isNotEmpty", label: "Is not empty" },
];

// Helper functions
export function getFieldType(fieldId: string): FieldType | null {
  const field = STOCK_TAKES_ALL_FIELDS.find((f) => f.id === fieldId);
  return field ? field.type : null;
}

export function getFieldLabel(fieldId: string): string {
  const field = STOCK_TAKES_ALL_FIELDS.find((f) => f.id === fieldId);
  return field ? field.label : fieldId;
}

export function getOperatorsForFieldType(fieldType: FieldType): OperatorOption[] {
  switch (fieldType) {
    case "text":
      return TEXT_OPERATORS;
    case "number":
      return NUMBER_OPERATORS;
    case "date":
      return DATE_OPERATORS;
    default:
      return [];
  }
}

export function getOperatorLabel(operator: FilterOperator): string {
  const allOperators = [
    ...TEXT_OPERATORS,
    ...NUMBER_OPERATORS,
    ...DATE_OPERATORS,
  ];
  const op = allOperators.find((o) => o.value === operator);
  return op ? op.label : operator;
}

export function operatorRequiresValue(operator: FilterOperator): boolean {
  return !["isEmpty", "isNotEmpty", "isYes", "isNo"].includes(operator);
}

export function operatorRequiresTwoValues(operator: FilterOperator): boolean {
  return operator === "between";
}

// Generate filter summary text
export function generateFilterSummary(
  rows: FilterRow[],
  logic: FilterLogic[]
): string {
  if (rows.length === 0) return "";

  const parts: string[] = [];

  rows.forEach((row, index) => {
    const fieldLabel = getFieldLabel(row.field);
    const operatorLabel = getOperatorLabel(row.operator).toLowerCase();
    
    let valuePart = "";
    if (operatorRequiresValue(row.operator)) {
      if (operatorRequiresTwoValues(row.operator)) {
        valuePart = ` ${formatValue(row.value)} and ${formatValue(row.value2)}`;
      } else if (row.operator === "inLastXDays") {
        valuePart = ` ${row.value} days`;
      } else {
        valuePart = ` ${formatValue(row.value)}`;
      }
    }

    parts.push(`${fieldLabel} ${operatorLabel}${valuePart}`);

    if (index < rows.length - 1 && logic[index]) {
      parts.push(` ${logic[index]} `);
    }
  });

  return `Show stock takes where ${parts.join("")}`;
}

function formatValue(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    return value.toLocaleDateString("en-GB");
  }
  return String(value);
}

// Generate active filter chips
export function generateFilterChips(
  rows: FilterRow[],
  logic: FilterLogic[]
): ActiveFilterChip[] {
  return rows.map((row, index) => {
    const fieldLabel = getFieldLabel(row.field);
    const operatorLabel = getOperatorLabel(row.operator);
    
    let displayText = `${fieldLabel} ${operatorLabel}`;
    
    if (operatorRequiresValue(row.operator)) {
      if (operatorRequiresTwoValues(row.operator)) {
        displayText += ` ${formatValue(row.value)} - ${formatValue(row.value2)}`;
      } else if (row.operator === "inLastXDays") {
        displayText += ` ${row.value} days`;
      } else {
        displayText += ` ${formatValue(row.value)}`;
      }
    }

    return {
      id: row.id,
      field: row.field,
      operator: row.operator,
      value: row.value,
      value2: row.value2,
      displayText,
    };
  });
}

// Validate filter row
export function validateFilterRow(row: FilterRow): string | null {
  if (!row.field) {
    return "Please select a field";
  }

  if (!row.operator) {
    return "Please select an operator";
  }

  if (operatorRequiresValue(row.operator)) {
    if (row.value === null || row.value === undefined || row.value === "") {
      return "Please enter a value";
    }

    if (operatorRequiresTwoValues(row.operator)) {
      if (row.value2 === null || row.value2 === undefined || row.value2 === "") {
        return "Please enter a second value";
      }

      // For number fields, validate range
      const fieldType = getFieldType(row.field);
      if (fieldType === "number") {
        const val1 = Number(row.value);
        const val2 = Number(row.value2);
        if (val1 >= val2) {
          return "First value must be less than second value";
        }
      }

      // For date fields, validate range
      if (fieldType === "date") {
        const date1 = row.value instanceof Date ? row.value : new Date(row.value);
        const date2 = row.value2 instanceof Date ? row.value2 : new Date(row.value2);
        if (date1 >= date2) {
          return "Start date must be before end date";
        }
      }
    }
  }

  return null;
}

// Apply filters to data
export function applyFilters<T extends Record<string, any>>(
  data: T[],
  rows: FilterRow[],
  logic: FilterLogic[]
): T[] {
  if (rows.length === 0) return data;

  return data.filter((item) => {
    let result = evaluateFilterRow(item, rows[0]);

    for (let i = 1; i < rows.length; i++) {
      const rowResult = evaluateFilterRow(item, rows[i]);
      const logicOperator = logic[i - 1] || "AND";

      if (logicOperator === "AND") {
        result = result && rowResult;
      } else {
        result = result || rowResult;
      }
    }

    return result;
  });
}

function evaluateFilterRow<T extends Record<string, any>>(
  item: T,
  row: FilterRow
): boolean {
  const fieldValue = item[row.field];
  const fieldType = getFieldType(row.field);

  if (!fieldType) return true;

  switch (fieldType) {
    case "text":
      return evaluateTextFilter(fieldValue, row.operator as TextOperator, row.value);
    case "number":
      return evaluateNumberFilter(fieldValue, row.operator as NumberOperator, row.value, row.value2);
    case "date":
      return evaluateDateFilter(fieldValue, row.operator as DateOperator, row.value, row.value2);
    default:
      return true;
  }
}

function evaluateTextFilter(
  fieldValue: any,
  operator: TextOperator,
  value: string | number | Date | null
): boolean {
  const strValue = String(fieldValue || "").toLowerCase();
  const compareValue = String(value || "").toLowerCase();

  switch (operator) {
    case "equals":
      return strValue === compareValue;
    case "notEquals":
      return strValue !== compareValue;
    case "contains":
      return strValue.includes(compareValue);
    case "doesNotContain":
      return !strValue.includes(compareValue);
    case "startsWith":
      return strValue.startsWith(compareValue);
    case "endsWith":
      return strValue.endsWith(compareValue);
    case "isEmpty":
      return !fieldValue || strValue === "";
    case "isNotEmpty":
      return !!fieldValue && strValue !== "";
    default:
      return true;
  }
}

function evaluateNumberFilter(
  fieldValue: any,
  operator: NumberOperator,
  value: string | number | Date | null,
  value2?: string | number | Date | null
): boolean {
  const numValue = Number(fieldValue);
  const compareValue = Number(value);
  const compareValue2 = Number(value2);

  if (operator === "isEmpty") {
    return fieldValue === null || fieldValue === undefined;
  }

  if (operator === "isNotEmpty") {
    return fieldValue !== null && fieldValue !== undefined;
  }

  if (isNaN(numValue)) return false;

  switch (operator) {
    case "equals":
      return numValue === compareValue;
    case "notEquals":
      return numValue !== compareValue;
    case "greaterThan":
      return numValue > compareValue;
    case "greaterThanOrEqual":
      return numValue >= compareValue;
    case "lessThan":
      return numValue < compareValue;
    case "lessThanOrEqual":
      return numValue <= compareValue;
    case "between":
      return numValue >= compareValue && numValue <= compareValue2;
    default:
      return true;
  }
}

function evaluateDateFilter(
  fieldValue: any,
  operator: DateOperator,
  value: string | number | Date | null,
  value2?: string | number | Date | null
): boolean {
  if (operator === "isEmpty") {
    return fieldValue === null || fieldValue === undefined;
  }

  if (operator === "isNotEmpty") {
    return fieldValue !== null && fieldValue !== undefined;
  }

  if (!fieldValue) return false;

  const dateValue = fieldValue instanceof Date ? fieldValue : new Date(fieldValue);
  const compareDate = value instanceof Date ? value : new Date(value!);
  const compareDate2 = value2 instanceof Date ? value2 : new Date(value2!);

  // Reset time parts for date comparison
  dateValue.setHours(0, 0, 0, 0);
  compareDate.setHours(0, 0, 0, 0);
  if (value2) compareDate2.setHours(0, 0, 0, 0);

  switch (operator) {
    case "equals":
      return dateValue.getTime() === compareDate.getTime();
    case "before":
      return dateValue.getTime() < compareDate.getTime();
    case "after":
      return dateValue.getTime() > compareDate.getTime();
    case "between":
      return dateValue.getTime() >= compareDate.getTime() && dateValue.getTime() <= compareDate2.getTime();
    case "inLastXDays":
      const daysAgo = Number(value);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      cutoffDate.setHours(0, 0, 0, 0);
      return dateValue.getTime() >= cutoffDate.getTime();
    default:
      return true;
  }
}
